import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { SITE } from "../data/site";

const maskLine = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1, delay: 0.2 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
    }),
};

const MaskedLine = ({ index, children }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span className="block" custom={index} variants={maskLine} initial="hidden" animate="show">
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const visualRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: visualRef, offset: ["start start", "end start"] });
    const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 110]);

    return (
        <section id="acasa" data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-36 lg:pt-40 pb-16 sm:pb-24">
            <div className="pointer-events-none absolute -top-24 -right-32 h-[28rem] w-[28rem] rounded-full bg-coral/15 blur-3xl" />
            <div className="pointer-events-none absolute top-64 -left-40 h-[24rem] w-[24rem] rounded-full bg-teal/10 blur-3xl" />

            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="inline-flex items-center gap-2 rounded-full bg-teal/5 px-4 py-2 font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-teal ring-1 ring-teal/15"
                        data-testid="hero-eyebrow"
                    >
                        {SITE.tagline}
                    </motion.p>

                    <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.06] text-charcoal" data-testid="hero-headline">
                        <MaskedLine index={0}>Ieși din spital.</MaskedLine>
                        <MaskedLine index={1}>
                            <em className="italic text-coral">Și acum?</em>
                        </MaskedLine>
                        <MaskedLine index={2}>
                            <span className="text-teal">Ține<span className="text-coral">(te)</span> de noi.</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-6 max-w-xl text-base sm:text-lg text-charcoal/75 leading-relaxed"
                        data-testid="hero-subtext"
                    >
                        Doi oameni pe care te poți baza: stabilim împreună ce ai nevoie, organizăm ajutorul și ne ținem de sarcinile convenite — ca să-ți vezi de viața ta.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
                    >
                        <a
                            href={SITE.phoneHref}
                            data-testid="hero-call-button"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-coral px-7 py-4 text-base font-semibold text-cream shadow-lg shadow-coral/25 transition-[background-color,transform] duration-300 hover:bg-coral-dark hover:-translate-y-0.5"
                        >
                            <Phone className="h-5 w-5" />
                            Sună: {SITE.phoneDisplay}
                        </a>
                        <a
                            href="#preturi"
                            data-testid="hero-pricing-button"
                            className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-base font-semibold text-teal ring-1 ring-teal/25 transition-[background-color,transform] duration-300 hover:bg-teal/5 hover:-translate-y-0.5"
                        >
                            Vezi pachetele
                            <ArrowDown className="h-4 w-4" />
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 1.1 }}
                        className="mt-5 text-sm text-charcoal/55"
                        data-testid="hero-note"
                    >
                        Prima discuție de potrivire este gratuită · maximum 15 minute
                    </motion.p>
                </div>

                <div ref={visualRef} className="lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="relative mx-auto max-w-sm lg:max-w-none"
                    >
                        <div className="absolute -top-6 -left-6 h-28 w-28 rounded-full bg-coral/25" />
                        <div className="relative overflow-hidden rounded-t-[10rem] rounded-b-[2rem] ring-1 ring-teal/10 shadow-2xl shadow-teal/15" data-testid="hero-image">
                            <motion.img
                                src="https://images.pexels.com/photos/18459702/pexels-photo-18459702.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                                alt="Un om ajutat acasă, cu căldură și răbdare"
                                style={{ y: parallaxY }}
                                className="h-[26rem] sm:h-[30rem] w-full scale-110 object-cover"
                            />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl bg-creamlight/95 backdrop-blur px-5 py-4 shadow-xl shadow-teal/10 ring-1 ring-teal/10"
                            data-testid="hero-floating-card"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-coral">Promisiunea noastră</p>
                            <p className="mt-1 font-serif text-lg font-semibold text-teal leading-snug">Revenim în 24–48h<br />de la sosirea acasă</p>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
